/**
 * CREDIT CARDS — how a card is paid, and what that costs (owner, 26 Oct 2026).
 *
 * A card has no term, so its repayment cannot be derived the way a mortgage's can, and a family
 * member filling in the profile rarely knows the figure. The profile therefore asks HOW the card is
 * paid rather than how much, and this module turns that answer into the month's payment and interest.
 * Before it, a card with no repayment typed in was modelled as costing nothing: no payment, no
 * interest, the balance standing still for the whole projection.
 *
 *   'full'    Paid off in full every month. No interest is charged; the balance is this month's
 *             spending, which is already in the living costs. It stays owed at the same figure (a
 *             rolling monthly bill) and draws no separate repayment.
 *   'minimum' Pays the minimum each month: interest accrues at the card's rate and the minimum is
 *             taken, recalculated each month on what is then owed.
 *   'fixed'   Pays a set amount each month: the amount entered (`repayments`).
 *   'unsure'  Not sure: treated as the minimum, and said to be an estimate.
 *
 * An older card with no answer is read as 'fixed' when a repayment was typed, else 'unsure'.
 *
 * THE MINIMUM. The greater of 2% of the closing balance or $25, and never more than is owed. This is
 * the rule of three of the four major banks for their standard personal cards:
 *   - CommBank: the greater of 2% of the closing balance or $25
 *     (commbank.com.au/articles/credit-cards/credit-card-minimum-repayment.html);
 *   - ANZ: 2% of the closing balance, or the lesser of $25 and the closing balance
 *     (ANZ credit cards key facts sheet);
 *   - NAB: 2% or $25, whichever is greater (nab.com.au/keyfactsheet);
 *   - Westpac: 2% or $10 today, moving to 2.5% or $25 from 30 November 2026
 *     (westpac.com.au/credit-cards/credit-card-changes.html).
 * Checked 26 Oct 2026.
 *
 * THE TYPICAL RATE, used when no rate is recorded: 20.99% a year, the RBA's average for standard
 * cards as quoted by Mozo and Finder in 2026 (mozo.com.au/credit-cards; finder.com.au "how much credit
 * card debt does the average Australian have"). Shown on the profile as typical, and editable.
 */

export type CardPaymentStyle = 'full' | 'minimum' | 'fixed' | 'unsure';

export const CARD_MINIMUM_PERCENT = 2;
export const CARD_MINIMUM_FLOOR = 25;
export const CARD_TYPICAL_RATE = 20.99;

interface CardLike {
    type?: string;
    balance?: number | string;
    interestRate?: number | string | null;
    repayments?: number | string;
    frequency?: string;
    cardPaymentStyle?: unknown;
}

/** True for a credit card — by its type, or by the bucket it sits in where the row has no type. */
export function isCreditCard(liability: { type?: unknown } | null | undefined, category?: string): boolean {
    return liability?.type === 'creditCards' || category === 'creditCards';
}

const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : 0;
};

/** How this card is paid, with an older card's missing answer read from what was typed. */
export function cardPaymentStyleOf(card: CardLike): CardPaymentStyle {
    const s = card.cardPaymentStyle;
    if (s === 'full' || s === 'minimum' || s === 'fixed' || s === 'unsure') return s;
    return num(card.repayments) > 0 ? 'fixed' : 'unsure';
}

/** True when no rate is recorded at all — blank, not a typed 0 (an interest-free card is a real thing). */
const rateIsBlank = (card: CardLike): boolean =>
    card.interestRate == null || String(card.interestRate).trim() === '' || !Number.isFinite(parseFloat(String(card.interestRate)));

/** True where the typical rate stands in because none is recorded (said so on the profile). */
export function cardRateIsTypical(card: CardLike): boolean {
    return cardPaymentStyleOf(card) !== 'full' && rateIsBlank(card);
}

/**
 * The yearly rate the card is modelled at: nothing when paid in full, the typical rate when none is
 * recorded, otherwise the rate entered (a typed 0 is an interest-free card, and is honoured).
 */
export function cardAnnualRate(card: CardLike): number {
    if (cardPaymentStyleOf(card) === 'full') return 0;
    return rateIsBlank(card) ? CARD_TYPICAL_RATE : Math.max(0, num(card.interestRate));
}

/** The minimum on a closing balance: the greater of 2% or $25, never more than is owed. */
export function cardMinimumPayment(closingBalance: number): number {
    if (!(closingBalance > 0)) return 0;
    return Math.min(closingBalance, Math.max((closingBalance * CARD_MINIMUM_PERCENT) / 100, CARD_MINIMUM_FLOOR));
}

/**
 * The set monthly amount of a 'fixed' card. The question asks "each month", so an answered card's
 * amount is monthly; an older row with no answer, typed at another frequency, is put on a monthly footing.
 */
export function cardFixedMonthly(card: CardLike): number {
    const amount = Math.max(0, num(card.repayments));
    if (card.cardPaymentStyle != null) return amount;
    if (card.frequency === 'weekly') return (amount * 52) / 12;
    if (card.frequency === 'fortnightly') return (amount * 26) / 12;
    return amount;
}

/**
 * One month on the card: interest on what is owed, then the payment (never more than the balance plus
 * that interest). A minimum is struck on the closing balance (owed plus the month's interest), as the
 * banks do. A 'full' card accrues nothing and pays nothing here — its spending is in the living costs.
 */
export function cardMonth(card: CardLike, owed: number): { interest: number; payment: number; closing: number } {
    const style = cardPaymentStyleOf(card);
    if (style === 'full' || !(owed > 0)) return { interest: 0, payment: 0, closing: Math.max(0, owed) };
    const interest = owed * (cardAnnualRate(card) / 100 / 12);
    const closingBeforePayment = owed + interest;
    const due = style === 'fixed' ? cardFixedMonthly(card) : cardMinimumPayment(closingBeforePayment);
    const payment = Math.min(due, closingBeforePayment);
    return { interest, payment, closing: closingBeforePayment - payment };
}

/** A year of months on the card from `openingBalance` — what is paid, what accrues, what is left. */
export function projectCardYear(card: CardLike, openingBalance: number, months = 12): { paid: number; interestAccrued: number; closingBalance: number; firstPayment: number } {
    let owed = openingBalance;
    let paid = 0;
    let interestAccrued = 0;
    let firstPayment = 0;
    for (let m = 0; m < months && owed > 0; m++) {
        const month = cardMonth(card, owed);
        if (m === 0) firstPayment = month.payment;
        paid += month.payment;
        interestAccrued += month.interest;
        owed = month.closing;
    }
    return { paid, interestAccrued, closingBalance: owed, firstPayment };
}

/** What the profile says under the answer. */
export interface CardPayoffSummary {
    style: CardPaymentStyle;
    /** The first month's payment. */
    monthlyPayment: number;
    /** The first month's interest. */
    monthlyInterest: number;
    /** True when the payment does not cover the month's interest, so what is owed grows. */
    grows: boolean;
    /** Months to clear it; null when it is paid in full, grows, or would take more than 50 years. */
    months: number | null;
    /** Interest paid on the way to clearing it. */
    totalInterest: number;
}

export function cardPayoffSummary(card: CardLike): CardPayoffSummary {
    const style = cardPaymentStyleOf(card);
    const balance = Math.max(0, num(card.balance));
    if (style === 'full' || balance <= 0) {
        return { style, monthlyPayment: 0, monthlyInterest: 0, grows: false, months: style === 'full' ? null : 0, totalInterest: 0 };
    }
    const first = cardMonth(card, balance);
    if (first.payment <= first.interest + 0.005) {
        return { style, monthlyPayment: first.payment, monthlyInterest: first.interest, grows: true, months: null, totalInterest: 0 };
    }
    let owed = balance;
    let totalInterest = 0;
    let months = 0;
    while (owed > 0.005 && months < 600) {
        const m = cardMonth(card, owed);
        totalInterest += m.interest;
        owed = m.closing;
        months++;
    }
    return { style, monthlyPayment: first.payment, monthlyInterest: first.interest, grows: false, months: owed > 0.005 ? null : months, totalInterest };
}

/**
 * The yearly rate a debt is modelled at, for any debt: a card's by {@link cardAnnualRate} (nothing when
 * paid in full, the typical rate when blank), anything else its recorded rate. The one figure every
 * "what holding it costs" line and every dearest-first payoff order reads, so none of them claims
 * interest the projection does not charge.
 */
export function liabilityInterestRate(liability: CardLike | null | undefined, category?: string): number {
    if (!liability) return 0;
    if (isCreditCard(liability, category)) return cardAnnualRate(liability);
    return num(liability.interestRate);
}
